import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["pressed", "defaultPressed", "onClick"];
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useState } from 'react';
import { StyledToggleCard } from './ToggleCard.styles';

/**
 * Toggle cards are an official
 * [WAI-ARIA button](https://www.w3.org/TR/wai-aria-practices/#button) type that use the
 * [aria-pressed](https://www.w3.org/TR/wai-aria-1.1/#aria-pressed) attribute to tell assistive
 * technologies that the card can be toggled like a button.
 *
 * **Important:** it is critical that content of the card
 * does not change when its state changes.
 *
 * @extends Card
 * @since 8.34.0
 */
export var ToggleCard = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var pressed = _ref.pressed,
    defaultPressed = _ref.defaultPressed,
    onClick = _ref.onClick,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(defaultPressed),
    _useState2 = _slicedToArray(_useState, 2),
    pressedState = _useState2[0],
    setPressedState = _useState2[1];
  var onClickCallback = useCallback(function (e) {
    setPressedState(!pressedState);
    if (onClick) {
      // for controlled components, we pass the current value of pressed
      //   (the toggling behavior is controlled by external logic)
      // for uncontrolled components, we pass the opposite of pressedState
      //   (because this component is where the toggling logic happens)
      onClick(e, typeof pressed !== 'undefined' ? pressed : !pressedState);
    }
  }, [onClick, pressed, pressedState]);

  // If pressed is defined, use that value as a controlled value, otherwise use local
  // uncontrolled state.
  var ariaPressed = pressedState;
  if (typeof pressed !== 'undefined') {
    ariaPressed = pressed;
  }
  return /*#__PURE__*/React.createElement(StyledToggleCard, _extends({
    "aria-pressed": !!ariaPressed,
    onClick: onClickCallback,
    role: "button",
    interactive: true,
    ref: forwardedRef
  }, rest));
});
ToggleCard.displayName = 'ToggleCard';
ToggleCard.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Sets the [aria-pressed](https://www.w3.org/TR/wai-aria-1.1/#aria-pressed) state and use as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   *
   * A toggle card can use `pressed` or `defaultPressed`, but not both.
   */
  defaultPressed: PropTypes.bool,
  /**
   * A click handler that will be called with two arguments, `e`, the button click event,
   * and `pressed`, the current pressed state.
   */
  onClick: PropTypes.func,
  /**
   * Sets the [aria-pressed](https://www.w3.org/TR/wai-aria-1.1/#aria-pressed) state and use as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * A toggle card can use `pressed` or `defaultPressed`, but not both.
   */
  pressed: PropTypes.bool
} : {};
ToggleCard.defaultProps = {
  defaultPressed: undefined,
  onClick: undefined,
  pressed: undefined
};